import { DateTime } from 'luxon';

import {
  resolvePreset,
  type DateRange,
  type DateRangePreset,
} from '@/components/shared/date-range-filter';

export interface OfferingsPagePreferences {
  dateRangePreset: DateRangePreset;
  dateFrom: string | null;
  dateTo: string | null;
}

const DEFAULT_PREFERENCES: OfferingsPagePreferences = {
  dateRangePreset: 'custom',
  dateFrom: null,
  dateTo: null,
};

const DATE_RANGE_PRESETS = new Set<DateRangePreset>([
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'thisYear',
  'lastYear',
  'allTime',
  'custom',
]);

function getPreferencesStorageKey(churchId?: string) {
  return churchId
    ? `giving-offerings-page-preferences:${churchId}`
    : 'giving-offerings-page-preferences';
}

export function defaultOfferingDateRange(timezone: string): DateRange {
  const today = DateTime.now().setZone(timezone);
  return {
    preset: 'custom',
    from: today.minus({ days: 30 }).toFormat('yyyy-MM-dd'),
    to: today.toFormat('yyyy-MM-dd'),
  };
}

export function resolveStoredOfferingDateRange(
  preferences: Pick<OfferingsPagePreferences, 'dateRangePreset' | 'dateFrom' | 'dateTo'>,
  timezone: string,
): DateRange {
  const preset = DATE_RANGE_PRESETS.has(preferences.dateRangePreset)
    ? preferences.dateRangePreset
    : 'custom';
  if (preset === 'custom') {
    if (preferences.dateFrom && preferences.dateTo) {
      return {
        preset: 'custom',
        from: preferences.dateFrom,
        to: preferences.dateTo,
      };
    }
    return defaultOfferingDateRange(timezone);
  }
  return {
    preset,
    ...resolvePreset(preset, timezone),
  };
}

export function loadOfferingsPagePreferences(churchId?: string): OfferingsPagePreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_PREFERENCES;
  }

  try {
    const raw = window.localStorage.getItem(getPreferencesStorageKey(churchId));
    if (!raw) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(raw) as Partial<OfferingsPagePreferences>;
    const dateRangePreset = DATE_RANGE_PRESETS.has(parsed.dateRangePreset as DateRangePreset)
      ? (parsed.dateRangePreset as DateRangePreset)
      : DEFAULT_PREFERENCES.dateRangePreset;

    return {
      dateRangePreset,
      dateFrom: typeof parsed.dateFrom === 'string' ? parsed.dateFrom : null,
      dateTo: typeof parsed.dateTo === 'string' ? parsed.dateTo : null,
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function persistOfferingsPagePreferences(
  churchId: string | undefined,
  preferences: OfferingsPagePreferences,
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getPreferencesStorageKey(churchId), JSON.stringify(preferences));
  } catch {
    // Ignore storage failures and fall back to the in-memory state for this session.
  }
}

export function toOfferingsPagePreferences(dateRange: DateRange): OfferingsPagePreferences {
  return {
    dateRangePreset: dateRange.preset,
    dateFrom: dateRange.from,
    dateTo: dateRange.to,
  };
}
